/**
 * People database: search anyone we've had a paper for (name, phone, street, plate, employer,
 * notes) and see everything in one place. Plus the address-history pieces used on the paper screen.
 */
import { useState, type ReactNode } from 'react';
import { EYE_COLORS, HAIR_COLORS, OUTCOME_LABELS } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, Empty, ErrorBox, Notice, PageHeader, Spinner, StatusBadge, useAction, useLoad } from '../shared/ui.tsx';

// A bare date (DOB) is a calendar day, not midnight UTC — don't let the time zone move it back a day.
const fmtD = (d: string) => new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
const fmtDT = (d: string) => new Date(d).toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' });
const inField = () => location.pathname.startsWith('/field');
const peopleBase = () => (inField() ? '/field/people' : '/people');
const profileUrl = (key: string) => `${peopleBase()}/profile?key=${encodeURIComponent(key)}`;
const reportUrl = (id: string) => `${inField() ? '/field/reports' : '/reports'}/${id}`;
const HIT_LABEL: Record<string, string> = { fields: 'the fields you filled in', name: 'name', phone: 'phone', address: 'address', vehicle: 'vehicle', employer: 'employer', alias: 'alias', note: 'note' };
const ROLE_LABEL: Record<string, string> = { victim: 'Victim', suspect: 'Suspect', arrestee: 'Arrested', witness: 'Witness', complainant: 'Complainant', other: 'Other' };
const ROLE_TONE: Record<string, 'bad' | 'warn' | 'info' | 'neutral'> = { arrestee: 'bad', suspect: 'warn', victim: 'info' };
const REPORT_STATUS: Record<string, string> = { draft: 'draft', submitted: 'waiting for approval', returned: 'sent back', approved: 'approved' };

/** "Arrested 2×", "Suspect in 3 reports", "Victim in 1 report" — the quick read on someone. */
export function RecordBadges({ r }: { r: { arrests: number; suspect: number; victim: number; armed?: boolean } }) {
  const n = (x: number, w: string) => `${x} ${w}${x === 1 ? '' : 's'}`;
  return (
    <>
      {r.arrests > 0 && <Badge tone="bad">Arrested {r.arrests}×</Badge>}
      {r.armed && <Badge tone="bad">Armed at arrest</Badge>}
      {r.suspect - r.arrests > 0 && <Badge tone="warn">Suspect in {n(r.suspect - r.arrests, 'report')}</Badge>}
      {r.victim > 0 && <Badge tone="info">Victim in {n(r.victim, 'report')}</Badge>}
    </>
  );
}

const PERSON_FIELDS = ['last', 'first', 'address', 'phone', 'height', 'weight', 'eyes', 'hair'] as const;
const VEHICLE_FIELDS = ['tag', 'vin', 'serial', 'year', 'make', 'model', 'color'] as const;

/** A labeled box in the detailed-search grid. */
function F({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`ps-f${wide ? ' wide' : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export function PeopleSearch() {
  const { path, navigate } = useRouter();
  const params = new URLSearchParams(path.split('?')[1] ?? '');
  const initial = params.get('q') ?? '';
  const vInitial = params.get('v') ?? '';
  const personQuery = PERSON_FIELDS.filter((k) => params.get(k)).map((k) => `${k}=${encodeURIComponent(params.get(k)!)}`).join('&');
  const vehicleQuery = VEHICLE_FIELDS.filter((k) => params.get(k)).map((k) => `${k}=${encodeURIComponent(params.get(k)!)}`).join('&');
  const [mode, setMode] = useState<'person' | 'vehicle'>(vInitial || vehicleQuery ? 'vehicle' : 'person');
  const [q, setQ] = useState(initial);
  const [v, setV] = useState(vInitial);
  const [pf, setPf] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = Object.fromEntries(PERSON_FIELDS.map((k) => [k, params.get(k) ?? '']));
    const h = Number(o.height);
    return { ...o, ft: h ? String(Math.floor(h / 12)) : '', in: h ? String(h % 12) : '' };
  });
  const [vf, setVf] = useState<Record<string, string>>(() => Object.fromEntries(VEHICLE_FIELDS.map((k) => [k, params.get(k) ?? ''])));
  const setP = (k: string) => (e: { target: { value: string } }) => setPf((x) => ({ ...x, [k]: e.target.value }));
  const setVv = (k: string) => (e: { target: { value: string } }) => setVf((x) => ({ ...x, [k]: e.target.value }));
  const searchUrl = personQuery ? `/api/people?${personQuery}` : initial.trim().length >= 2 ? `/api/people?q=${encodeURIComponent(initial)}` : null;
  const res = useLoad(() => (searchUrl ? api.get<any>(searchUrl) : Promise.resolve(null)), [searchUrl]);
  const civil = res.data ? res.data.people.some((p: any) => p.papers > 0) || !res.data.rms : true;
  const rms = !!res.data?.rms;
  const asked = personQuery
    ? [pf.last && pf.first ? `${pf.first} ${pf.last}` : pf.last || pf.first, pf.address, pf.phone, params.get('height') && `${pf.ft}′${pf.in || 0}″`, params.get('weight') && `${params.get('weight')} lb`, params.get('eyes') && `${params.get('eyes')} eyes`, params.get('hair') && `${params.get('hair')} hair`].filter(Boolean).join(', ')
    : initial;
  const runPerson = () => {
    const height = pf.ft ? Number(pf.ft) * 12 + (Number(pf.in) || 0) : 0;
    const out = { ...pf, height: height ? String(height) : '' };
    const qs = PERSON_FIELDS.filter((k) => (out[k] ?? '').trim()).map((k) => `${k}=${encodeURIComponent(out[k]!.trim())}`).join('&');
    if (qs) navigate(`${peopleBase()}?${qs}`);
  };
  const runVehicle = () => {
    const qs = VEHICLE_FIELDS.filter((k) => (vf[k] ?? '').trim()).map((k) => `${k}=${encodeURIComponent(vf[k]!.trim())}`).join('&');
    if (qs) navigate(`${peopleBase()}?${qs}`);
  };
  const clearPerson = () => (setPf(Object.fromEntries([...PERSON_FIELDS, 'ft', 'in'].map((k) => [k, '']))), navigate(peopleBase()));
  const clearVehicle = () => (setVf(Object.fromEntries(VEHICLE_FIELDS.map((k) => [k, '']))), navigate(peopleBase()));
  return (
    <div className="stack">
      <PageHeader crumb="Records" title="People & vehicles" sub="Everyone we've dealt with — civil papers, incident reports and arrests — and every tag, VIN and serial number we've written down." />
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={mode === 'person'} className={mode === 'person' ? 'active' : ''} onClick={() => setMode('person')}>
          Person
        </button>
        <button role="tab" aria-selected={mode === 'vehicle'} className={mode === 'vehicle' ? 'active' : ''} onClick={() => setMode('vehicle')}>
          Vehicle or property
        </button>
      </div>
      {mode === 'person' ? (
        <>
          <form
            className="card ps-card"
            onSubmit={(e) => {
              e.preventDefault();
              runPerson();
            }}
          >
            <div className="ps-grid">
              <F label="Last name">
                <input value={pf.last} onChange={setP('last')} autoComplete="off" autoFocus />
              </F>
              <F label="First name">
                <input value={pf.first} onChange={setP('first')} autoComplete="off" />
              </F>
              <F label="Address">
                <input value={pf.address} onChange={setP('address')} placeholder="123 Main St" autoComplete="off" />
              </F>
              <F label="Phone">
                <input value={pf.phone} onChange={setP('phone')} inputMode="tel" placeholder="803-555-0199" autoComplete="off" />
              </F>
              <div className="ps-f">
                <span>Height</span>
                <span className="ps-hgt">
                  <input aria-label="Height feet" value={pf.ft} onChange={setP('ft')} inputMode="numeric" placeholder="ft" />
                  <input aria-label="Height inches" value={pf.in} onChange={setP('in')} inputMode="numeric" placeholder="in" />
                </span>
              </div>
              <F label="Weight (lb)">
                <input value={pf.weight} onChange={setP('weight')} inputMode="numeric" placeholder="about" />
              </F>
              <F label="Eyes">
                <select value={pf.eyes} onChange={setP('eyes')}>
                  <option value="">Any</option>
                  {EYE_COLORS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </F>
              <F label="Hair">
                <select value={pf.hair} onChange={setP('hair')}>
                  <option value="">Any</option>
                  {HAIR_COLORS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </F>
            </div>
            <div className="ps-foot">
              <span className="small muted">Fill in any boxes. Height matches within 2 inches and weight within 15 lb, from reports.</span>
              <span className="spacer" />
              <button type="button" className="btn ghost" onClick={clearPerson}>
                Clear
              </button>
              <button className="btn primary">Search</button>
            </div>
          </form>
          <form
            className="row gap ps-quick"
            onSubmit={(e) => {
              e.preventDefault();
              navigate(`${peopleBase()}?q=${encodeURIComponent(q.trim())}`);
            }}
          >
            <input className="grow people-q" aria-label="Search people" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Or search everything in one box: John Doe · 803-555-0199 · 123 Main St · ABC123" />
            <button className="btn">Search</button>
          </form>
          <ErrorBox error={res.error} />
          {res.loading && searchUrl ? (
            <Spinner />
          ) : res.data ? (
            res.data.people.length === 0 ? (
              <Empty>No one found for {asked ? `“${asked}”` : 'that'}.</Empty>
            ) : (
              <Card>
                <div className="table-wrap">
                  <table className="table people-results">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>DOB</th>
                        {civil && <th>Papers</th>}
                        {rms && <th>Reports</th>}
                        <th>Last address</th>
                        <th>Last seen</th>
                        <th>Matched on</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.data.people.map((p: any) => (
                        <tr key={p.name_key} className="clickable" onClick={() => navigate(profileUrl(p.name_key))}>
                          <td>
                            <Link to={profileUrl(p.name_key)}>
                              <strong>{p.name}</strong>
                            </Link>
                            {rms && (
                              <div className="row gap wrap people-badges">
                                <RecordBadges r={p} />
                              </div>
                            )}
                          </td>
                          <td>{p.dobs.length ? p.dobs.map(fmtD).join(', ') : <span className="muted">—</span>}</td>
                          {civil && <td>{p.papers}</td>}
                          {rms && <td>{p.reports}</td>}
                          <td>{p.last_address || <span className="muted">—</span>}</td>
                          <td>{p.last_at ? fmtD(p.last_at) : '—'}</td>
                          <td className="small muted">{p.hits.map((h: string) => HIT_LABEL[h] ?? h).join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )
          ) : null}
        </>
      ) : (
        <>
          <form
            className="card ps-card"
            onSubmit={(e) => {
              e.preventDefault();
              runVehicle();
            }}
          >
            <div className="ps-grid">
              <F label="Tag">
                <input className="mono" value={vf.tag} onChange={(e) => setVf((x) => ({ ...x, tag: e.target.value.toUpperCase() }))} autoComplete="off" autoFocus />
              </F>
              <F label="VIN" wide>
                <input className="mono" value={vf.vin} onChange={(e) => setVf((x) => ({ ...x, vin: e.target.value.toUpperCase() }))} autoComplete="off" placeholder="All or part" />
              </F>
              <F label="Serial #">
                <input className="mono" value={vf.serial} onChange={(e) => setVf((x) => ({ ...x, serial: e.target.value.toUpperCase() }))} autoComplete="off" />
              </F>
              <F label="Year">
                <input value={vf.year} onChange={setVv('year')} inputMode="numeric" placeholder="2015" autoComplete="off" />
              </F>
              <F label="Make">
                <input value={vf.make} onChange={setVv('make')} placeholder="Ford, Husqvarna…" autoComplete="off" />
              </F>
              <F label="Model">
                <input value={vf.model} onChange={setVv('model')} placeholder="F-150" autoComplete="off" />
              </F>
              <F label="Color">
                <input value={vf.color} onChange={setVv('color')} placeholder="White" autoComplete="off" />
              </F>
            </div>
            <div className="ps-foot">
              <span className="small muted">Fill in any boxes. Part of a tag, VIN or serial is fine. Make, model and color find vehicles and property in reports and seen at doors.</span>
              <span className="spacer" />
              <button type="button" className="btn ghost" onClick={clearVehicle}>
                Clear
              </button>
              <button className="btn primary">Look up</button>
            </div>
          </form>
          <form
            className="row gap ps-quick"
            onSubmit={(e) => {
              e.preventDefault();
              navigate(`${peopleBase()}?v=${encodeURIComponent(v.trim())}`);
            }}
          >
            <input className="grow people-q mono" aria-label="Tag, VIN or serial number" value={v} onChange={(e) => setV(e.target.value.toUpperCase())} placeholder="Or one box: tag, VIN or serial # — part of one is fine" />
            <button className="btn">Look up</button>
          </form>
          {(vInitial || vehicleQuery) && <VehicleResults query={vehicleQuery || `q=${encodeURIComponent(vInitial)}`} />}
        </>
      )}
    </div>
  );
}

function VehicleResults({ query }: { query: string }) {
  const d = useLoad(() => api.get<any>(`/api/vehicles?${query}`), [query]);
  if (d.error) return <ErrorBox error={d.error} />;
  if (!d.data) return <Spinner />;
  const x = d.data;
  if (x.enough === false) return <Empty>Type at least 3 letters or numbers of a tag, VIN or serial number — or a make or model, or two things like make and color.</Empty>;
  const nothing = !x.reports.length && !x.sightings.length && !x.noted.length;
  return (
    <div className="stack">
      {x.stolen.map((s: any) => (
        <div key={s.id} className="alert bad veh-stolen" role="alert">
          <strong>STOLEN</strong> — {s.vehicle || 'Item'} {s.plate && <span className="mono">tag {s.plate}</span>} {s.vin && <span className="mono">VIN {s.vin}</span>} {s.serial && <span className="mono">serial {s.serial}</span>}
          <div className="small">
            Reported stolen {fmtD(s.at)} on <Link to={reportUrl(s.incidentId)}>{s.caseNumber}</Link>
            {s.owner ? ` · owner ${s.owner}` : ''}. No recovery on file.
          </div>
        </div>
      ))}
      {nothing && <Empty>Nothing on file for “{x.q}”.</Empty>}
      {x.reports.length > 0 && (
        <Card title={`In reports (${x.reports.length})`}>
          <ul className="plain veh-list">
            {x.reports.map((r: any, i: number) => (
              <li key={i}>
                <div className="row gap wrap">
                  <Badge tone={r.loss === '7' ? 'bad' : r.loss === '5' ? 'ok' : r.loss === '6' ? 'warn' : 'neutral'}>{r.lossLabel}</Badge>
                  <strong>{r.vehicle || r.type}</strong>
                  {r.plate && <span className="mono">tag {r.plate}</span>}
                  {r.vin && <span className="mono small">VIN {r.vin}</span>}
                  {r.serial && <span className="mono small">serial {r.serial}</span>}
                  {r.evidence && <Badge tone="info">evidence</Badge>}
                </div>
                <div className="small muted">
                  <Link to={reportUrl(r.incidentId)}>{r.caseNumber}</Link> · {r.title} · {fmtD(r.at)}
                  {r.where ? ` · ${r.where}` : ''}
                  {r.owner ? ` · owner ${r.owner}` : ''} · {r.officer}
                  {r.status !== 'approved' ? ` · ${REPORT_STATUS[r.status] ?? r.status}` : ''}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {x.sightings.length > 0 && (
        <Card title={`Seen at a door on civil service (${x.sightings.length})`}>
          <ul className="plain veh-list">
            {x.sightings.map((s: any, i: number) => (
              <li key={i}>
                <strong>{s.vehicle}</strong>
                <div className="small muted">
                  {fmtDT(s.at)} · {s.where} · {s.server} · serving <Link to={profileUrl(s.nameKey)}>{s.person}</Link> ·{' '}
                  <Link to={`/papers/${s.paperId}`} className="mono">
                    {s.civilosNumber}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {x.noted.length > 0 && (
        <Card title={`Noted on a person (${x.noted.length})`}>
          <ul className="plain veh-list">
            {x.noted.map((n: any, i: number) => (
              <li key={i}>
                <Link to={profileUrl(n.nameKey)}>
                  <strong>{n.person}</strong>
                </Link>{' '}
                — {n.value} <span className="small muted">({n.source})</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

const KINDS = [
  ['phone', 'Phone'],
  ['address', 'Address'],
  ['vehicle', 'Vehicle'],
  ['employer', 'Employer'],
  ['alias', 'Also known as'],
  ['note', 'Note'],
] as const;

export function PersonProfile() {
  const { path } = useRouter();
  const { can } = useAuth();
  const key = new URLSearchParams(path.split('?')[1] ?? '').get('key') ?? '';
  const d = useLoad(() => api.get<any>(`/api/people/profile?key=${encodeURIComponent(key)}`), [key]);
  const [kind, setKind] = useState<string>('phone');
  const [value, setValue] = useState('');
  const a = useAction();
  if (d.error) return <ErrorBox error={d.error} />;
  if (!d.data) return <Spinner />;
  const p = d.data;
  // Civil sections only when there's civil history (someone known only from reports doesn't get empty paper tables).
  const showCivil = p.civil && (p.papers.length > 0 || p.attempts.length > 0);
  const add = () =>
    a.run(async () => {
      await api.post('/api/people/info', { key, kind, value: value.trim() });
      setValue('');
      d.reload();
    });
  const remove = (id: string) => {
    const reason = window.prompt('Why remove this?');
    if (reason) a.run(async () => (await api.post(`/api/people/info/${id}/remove`, { reason }), d.reload()));
  };
  const factList = (k: string, label: string) => {
    const items = p.facts[k] ?? [];
    return (
      <div className="fact-group" key={k}>
        <div className="fact-k">{label}</div>
        {items.length === 0 ? (
          <div className="muted small">—</div>
        ) : (
          <ul className="plain">
            {items.map((f: any, i: number) => (
              <li key={i} className="fact">
                <span>{f.value}</span>
                <span className="muted small">
                  {' '}
                  · {f.source}
                  {!f.source.startsWith('In the yard') && `, ${fmtD(f.at)}`}
                </span>
                {f.infoId && can('papers.intake') && (
                  <button className="linkish small" onClick={() => remove(f.infoId)}>
                    remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="stack">
      <div className="rec-head person-head">
        <div className="crumb">
          <Link to={peopleBase()}>People &amp; vehicles</Link> ›
        </div>
        <div className="rec-title">
          <h1>{p.name}</h1>
        </div>
        {p.rms && (
          <div className="row gap wrap people-badges">
            <RecordBadges r={p.record} />
          </div>
        )}
        <div className="muted">
          {p.dobs.length ? `DOB ${p.dobs.map(fmtD).join(' / ')}` : 'No DOB on file'}
          {showCivil && ` · ${p.papers.length} paper${p.papers.length === 1 ? '' : 's'}`}
          {p.rms && ` · ${p.record.reports} report${p.record.reports === 1 ? '' : 's'}`}
          {p.names.length > 1 && <> · Also written as {p.names.filter((n: string) => n !== p.name).join('; ')}</>}
        </div>
        <div style={{ height: 10 }} />
      </div>
      {p.dobConflict && <Notice tone="warn">Different dates of birth are on file under this name. These may be different people. Check the DOB on each paper and report below.</Notice>}
      {p.alerts.length > 0 && (
        <div className="alert bad">
          <strong>Safety alerts</strong>
          {p.alerts.map((x: any, i: number) => (
            <div key={i}>
              <strong>{x.category}</strong>
              {x.details ? ` — ${x.details}` : ''} <span className="small">({x.target === 'person' ? 'this person' : x.address_label ?? 'address'})</span>
            </div>
          ))}
        </div>
      )}
      <AddressFlagsBanner flags={p.addressFlags} title="At their latest address" />

      {p.rms && <ReportsCard reports={p.reports} name={p.name} />}

      <div className="grid2 top">
        <Card title="What we know">
          {KINDS.map(([k, label]) => factList(k, label))}
          {can('papers.intake') && showCivil && (
            <form
              className="row gap wrap add-fact"
              onSubmit={(e) => {
                e.preventDefault();
                if (value.trim()) add();
              }}
            >
              <select aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                {KINDS.map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
              <input aria-label="Value" className="grow" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Add a phone number, address, vehicle, note…" data-unsaved={value ? 'true' : undefined} />
              <button className="btn" disabled={a.busy || !value.trim()}>
                Add
              </button>
            </form>
          )}
          <ErrorBox error={a.error} />
        </Card>
        {showCivil && (
        <div className="stack">
          <Card title="Service record">
            <div className="stat-row">
              <div>
                <div className="stat-n">{p.stats.personal}</div>
                <div className="muted small">personal</div>
              </div>
              <div>
                <div className="stat-n">{p.stats.substitute}</div>
                <div className="muted small">substitute</div>
              </div>
              <div>
                <div className="stat-n">{p.stats.attempts}</div>
                <div className="muted small">attempts</div>
              </div>
            </div>
            <div className="small">
              <strong>Best time:</strong> {p.stats.bestTime}
            </div>
          </Card>
          <Card title="Known associates">
            {p.associates.length === 0 ? (
              <Empty>No one else has been served for this person.</Empty>
            ) : (
              <ul className="plain">
                {p.associates.map((x: any, i: number) => (
                  <li key={i}>
                    <strong>{x.name}</strong>
                    {x.relationship ? ` (${x.relationship})` : ''} <span className="muted small">· accepted service at {x.where}, {fmtD(x.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
        )}
      </div>

      {showCivil && (
        <>
      <Card title={`Papers (${p.papers.length})`}>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Paper</th>
                <th>Type</th>
                <th>Received</th>
                <th>Address</th>
                <th>Case / plaintiff</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {p.papers.map((x: any) => (
                <tr key={x.paperId}>
                  <td>
                    <Link to={`/papers/${x.paperId}`} className="mono">
                      {x.civilosNumber}
                    </Link>
                    {x.nameOnPaper !== p.name && <div className="small muted">as {x.nameOnPaper}</div>}
                  </td>
                  <td>{x.paperType}</td>
                  <td>{fmtD(x.receivedAt)}</td>
                  <td>{x.address}</td>
                  <td className="small">
                    <span className="mono">{x.caseNumber}</span>
                    {x.plaintiff && <div className="muted">{x.plaintiff}</div>}
                  </td>
                  <td>
                    <StatusBadge status={x.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title={`Attempts (${p.attempts.length})`}>
        {p.attempts.length === 0 ? (
          <Empty>No attempts yet.</Empty>
        ) : (
          <ul className="plain attempts-list">
            {p.attempts.map((x: any, i: number) => (
              <li key={i}>
                <strong>{fmtDT(x.attempted_at)}</strong> — {OUTCOME_LABELS[x.outcome as keyof typeof OUTCOME_LABELS] ?? x.outcome}
                {x.method ? ` (${x.method})` : ''} · {x.line1} · {x.server_name} · <span className="mono small">{x.civilos_number}</span>
                {x.notes && <div className="small muted">{x.notes}</div>}
              </li>
            ))}
          </ul>
        )}
      </Card>
        </>
      )}
    </div>
  );
}

function ReportsCard({ reports, name }: { reports: any[]; name: string }) {
  const { navigate } = useRouter();
  return (
    <Card title={`Reports & arrests (${reports.length})`}>
      {reports.length === 0 ? (
        <Empty>{name} isn't in any reports.</Empty>
      ) : (
        <ul className="plain pr-list">
          {reports.map((x) => (
            <li key={x.incidentId}>
              <button type="button" onClick={() => navigate(reportUrl(x.incidentId))}>
                {x.photoId && <img className="pr-photo" alt="" src={`/api/reports/photos/${x.photoId}`} />}
                <div className="pr-main">
                  <div className="row gap wrap">
                    {x.roles.map((r: string) => (
                      <Badge key={r} tone={ROLE_TONE[r] ?? 'neutral'}>
                        {ROLE_LABEL[r] ?? r}
                      </Badge>
                    ))}
                    <strong>{x.title}</strong>
                  </div>
                  <div className="small muted">
                    <span className="mono">{x.caseNumber}</span> · {fmtD(x.at)}
                    {x.where ? ` · ${x.where}` : ''} · {x.officer}
                    {x.status !== 'approved' ? ` · ${REPORT_STATUS[x.status] ?? x.status}` : ''}
                    {x.name !== name ? ` · as ${x.name}` : ''}
                  </div>
                  {x.arrest && (
                    <div className="small pr-arrest">
                      Arrested{x.arrest.at ? ` ${fmtDT(x.arrest.at)}` : ''}
                      {x.arrest.charge ? ` — ${x.arrest.charge}` : ''}
                      {x.arrest.localCharges.length ? ` · ${x.arrest.localCharges.join('; ')}` : ''}
                      {x.arrest.bookingNumber ? ` · booking ${x.arrest.bookingNumber}` : ''}
                      {x.arrest.warrant ? ` · warrant ${x.arrest.warrant}` : ''}
                      {x.arrest.armed && <strong> · ARMED</strong>}
                    </div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ─── Address history (paper screen, intake result) ─────────────────────────────

const FLAG_TONE: Record<string, string> = { evicted: 'bad', eviction_case: 'warn', vacant: 'warn', moved: 'warn', deceased: 'bad', bad_address: 'warn' };

export function AddressFlagsBanner({ flags, title = 'Address history' }: { flags: { kind: string; text: string; civilosNumber?: string; paperId?: string }[]; title?: string }) {
  if (!flags?.length) return null;
  const tone = flags.some((f) => FLAG_TONE[f.kind] === 'bad') ? 'bad' : 'warn';
  return (
    <div className={`addr-flags ${tone}`} role="alert">
      <div className="addr-flags-title">⚠ {title}</div>
      {flags.map((f, i) => (
        <div key={i}>
          {f.text}
          {f.paperId && (
            <>
              {' '}
              <Link to={`/papers/${f.paperId}`} className="small mono">
                {f.civilosNumber}
              </Link>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export function AddressHistoryCard({ history }: { history: { papers: any[] } | undefined }) {
  if (!history) return null;
  return (
    <Card title="Other papers at this address">
      {history.papers.length === 0 ? (
        <Empty>No other papers at this address in the last 2 years.</Empty>
      ) : (
        <ul className="plain addr-hist">
          {history.papers.map((x: any) => (
            <li key={x.paperId}>
              <Link to={`/papers/${x.paperId}`} className="mono">
                {x.civilosNumber}
              </Link>{' '}
              {x.defendant} {x.samePerson && <Badge tone="info">same person</Badge>}
              <div className="small muted">
                {x.paperType} · received {fmtD(x.receivedAt)} · {x.result}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
